import { EquipmentIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import { DemographicIcon, getDemographicById, getDemographicByUnitId } from "common";
import { sortBy } from "lodash";
import React from "react";
import { Experience, LineItem, LineItemType, Permission, PurchasedUnit, Unit } from "types";
import { useResource } from "../../../hooks/useResource";
import { isDefaultDemographic } from "../../../models/DemographicUnit";
import { IsGranted } from "../IsGranted";
import { NoTranslate } from "../NoTranslate";

interface BannerDemographicListProps {
    demographics: LineItem[] | PurchasedUnit[];
    experience: Experience;
}

export const BannerDemographicList = ({ demographics, experience }: BannerDemographicListProps) => {
    const data = demographics.map((demographic) => {
        const catalogItem =
            "template" in demographic
                ? getDemographicById(experience, demographic.template.id)
                : getDemographicByUnitId(experience, demographic.id);

        return {
            ...demographic,
            sequence: catalogItem?.sequence,
        };
    });

    return (
        <IsGranted permissions={Permission.DEMOGRAPHIC_LIST_VIEW}>
            <span className="space-x-2" data-testid="banner-demographics-list">
                {sortBy(data, "sequence").map((demographic: LineItem | PurchasedUnit) => {
                    const isPurchasedUnit = "count" in demographic;
                    const count = isPurchasedUnit ? demographic.count : demographic.quantity;

                    if (!count) {
                        return null;
                    }

                    return (
                        <span key={demographic.id}>
                            {isPurchasedUnit ? (
                                <DemographicItem demographic={demographic} count={count} />
                            ) : (
                                <DemographicLineItem demographic={demographic} count={count} />
                            )}
                        </span>
                    );
                })}
            </span>
        </IsGranted>
    );
};

const DemographicItem = ({ demographic, count }: { demographic: PurchasedUnit; count: number }) => {
    const { data: demographicUnit } = useResource<Unit>(["units", demographic.id]);

    return demographicUnit?.name ? (
        <Tooltip
            content={<NoTranslate if={!isDefaultDemographic(demographicUnit.name)}>{demographicUnit.name}</NoTranslate>}
        >
            <DemographicIcon name={demographicUnit.name} /> {count}
        </Tooltip>
    ) : null;
};

const DemographicLineItem = ({ demographic, count }: { demographic: LineItem; count: number }) => {
    const isEquipment = demographic.type === LineItemType.EQUIPMENT;
    return (
        <Tooltip
            content={
                <NoTranslate if={isEquipment || !isDefaultDemographic(demographic.name)}>
                    {demographic.name}
                </NoTranslate>
            }
        >
            {isEquipment ? <EquipmentIcon /> : <DemographicIcon name={demographic.name} />} {count}
        </Tooltip>
    );
};
